import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import { TableColumn } from 'src/app/models/table-column.model';
import { LoadingService } from 'src/app/services/loading.service';
import { ComplianceCategorySummaryWithIddProgressModel } from 'src/app/models/compliance/compliance-category-summary-with-idd-progress.model';
import { ComplianceCategoryService } from 'src/app/services/compliance-category.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-manage-compliance-overview',
  standalone: false,
  templateUrl: './manage-compliance-overview.component.html',
  styleUrl: './manage-compliance-overview.component.scss',
})
export class ManageComplianceOverviewComponent implements OnInit {
  allComplianceCategories$!: Observable<
    ComplianceCategorySummaryWithIddProgressModel[]
  >;

  columns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'name',
      cssClass: 'width-250',
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DESCRIPTION'),
      dataField: 'description',
      cssClass: 'width-350',
      disableFiltering: true,
    },
    {
      caption: this.translateService.instant('ADMINISTRATION.PROGRESS-PERCENT'),
      dataField: 'courseIddProgress.progressPercentage',
      dataType: 'string',
      name: 'progressPercentage',
      calculateSortValue: (data: ComplianceCategorySummaryWithIddProgressModel): string|number => {
         // deployed -> progress, not deployed -> -1, no course -> -2
        return data.courseIddProgress
          ? (data.courseIddProgress.isDeployed ? data.courseIddProgress.progressPercentage : -1)
          : -2;
      },
      calculateDisplayValue: (data: ComplianceCategorySummaryWithIddProgressModel): string => {
        if(!data.courseIddProgress) {
          return 'N/A';
        } else {
          return data.courseIddProgress.isDeployed 
            ? data.courseIddProgress.progressPercentage + ' %'
            : this.translateService.instant('COMMON.NOT-DEPLOYED')
        }
      },
      disableFiltering: true,
    },
    {
      caption: '',
      dataField: 'courseIddProgress.progressPercentage',
      dataType: 'string',
      name: 'progressPercentageBar',
      cellTemplate: 'progressBullet',
      disableFiltering: true,
      cellValue: (data: ComplianceCategorySummaryWithIddProgressModel): number|null => {
        if(data.courseIddProgress && data.courseIddProgress.isDeployed) {
          return data.courseIddProgress.progressPercentage;
        } else {
          return null;
        }
      },
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          icon: 'link-tab',
          isShowAction: () => true,
          onClick: (complianceCategoryWithIdd: ComplianceCategorySummaryWithIddProgressModel, e: any): void => {
            e.stopPropagation();
            e.preventDefault(); 
            e.stopImmediatePropagation();
            this.navigateToComplianceNewTab(complianceCategoryWithIdd.id);
          },
        },
      ],
    },
  ];
  
  readonly stateKey = 'compliance-categories-overview';

  constructor(
    private complianceCategoryService: ComplianceCategoryService,
    private loadingService: LoadingService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
     if (!this.pageStateService.consumeFlag(
      PageStateFlags.RestoreComplianceCategoriesOverview
    )) {
      this.pageStateService.clear(this.stateKey);
    }

    const deployedParam = this.route.snapshot.queryParamMap.get('deployed');
    const deployed = deployedParam !== null ? deployedParam === 'true' : null;

    this.loadComplianceList(deployed);
  }

  loadComplianceList(deployed: boolean | null = null) {
    this.allComplianceCategories$ = this.loadingService.load(
      this.complianceCategoryService.getMyComplianceCategoriesWithIddProgress()
    )
    .pipe(
      map(categories => {
        if (deployed === null) return categories;

        return categories.filter(c =>
          c.courseIddProgress && 
          c.courseIddProgress.isDeployed === deployed
        );
      })
    );
  }

  navigateToComplianceNewTab(id: string) {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['manage-compliance', id])
    );
    window.open(url, '_blank');
  }

  navigateToCompliance(id: string) {
    this.router.navigate(['manage-compliance', id]);
  }

  navigateToNewCompliance() {
    this.router.navigate(['manage-compliance', 'new']);
  }
}
